import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import {
  useChowProfileSnapshot,
  useDishFavouriteSnapshot,
  useDishSnapshot,
  useVendorLocationSnapshot,
  useVendorProfileSnapshot,
} from '../../generated/prototypeRuntime'
import { allowSeedContent, commandPayload, commands, isLiveRuntime } from '../../lib/runtime'
import { isVendorOpen, parseSchedule, type WeeklySchedule } from '../../lib/vendorSchedule'
import {
  dishes as curatedDishes,
  stores as curatedStores,
  type DishCategory,
  type Store,
} from './content'
import { canonicalSlug, slugify } from '../../lib/slug'
import { usePublicDishes } from '../../lib/discover'
import { chefPhoto, dishPhoto } from './dishImages'
import { readVerificationPaused } from './mealCategories'
import { displayTitle } from '../../lib/displayName'

/**
 * chef/vendor profile id → whether that kitchen is open *right now* (manual
 * toggle AND inside working hours). Every customer surface that needs to gate
 * ordering on the kitchen being open reads this, so the sign the customer sees
 * matches what the chef's own dashboard shows. Unknown ids default to open, so
 * a missing vendor row never silently blocks an order.
 */
/** Preparation time comes from the kitchen. Delivery timing requires a quote. */
export const dishCardEta = (cookTimeMinutes = 0): string =>
  cookTimeMinutes > 0 ? `${cookTimeMinutes} min prep` : ''

export const useChefOpenMap = (): Map<string, boolean> => {
  const vendors = useVendorProfileSnapshot() as { records: readonly Record<string, unknown>[] }
  return useMemo(() => {
    const map = new Map<string, boolean>()
    for (const v of vendors.records) {
      const id = String(v.profile_id ?? v.id ?? '')
      if (id) map.set(id, isVendorOpen(v))
    }
    return map
  }, [vendors.records])
}

/** A kitchen's public verification state, from the vendor projection. */
export interface ChefVerification {
  /** unverified | pending | verified | suspended | rejected | revoked */
  status: string
  /** none | verified | hygiene_certified | top_kitchen (server-derived). */
  badge: string
  sickPaused: boolean
}

/** Suspensions remain excluded while verification intake is paused. */
export const isSellingKitchen = (entry: ChefVerification | undefined): boolean =>
  Boolean(entry && ['verified', 'unverified', 'pending'].includes(entry.status))

/**
 * `chef profile_id → verification state`. The server enforces the same rule on
 * every order write (marketplace sales gate); this keeps the catalogue from
 * showing a kitchen the server would refuse, so "Add" never fails silently.
 */
export const useChefVerification = (): Map<string, ChefVerification> => {
  const vendors = useVendorProfileSnapshot() as { records: readonly Record<string, unknown>[] }
  const [verificationRequired, setVerificationRequired] = useState(false)
  useEffect(() => {
    let active = true
    void readVerificationPaused().then((paused) => {
      if (active) setVerificationRequired(paused === false)
    }).catch(() => {})
    return () => { active = false }
  }, [])
  return useMemo(() => {
    const map = new Map<string, ChefVerification>()
    for (const v of vendors.records) {
      const id = String(v.profile_id ?? '')
      if (!id) continue
      const statusTail = enumTail(v.verification_status)
      map.set(id, {
        status: verificationRequired && statusTail !== 'verified' && statusTail !== ''
          ? 'verification_required' : (statusTail || 'unverified'),
        badge: String(v.badge_tier ?? 'none'),
        sickPaused: v.is_sick_paused === true,
      })
    }
    return map
  }, [vendors.records, verificationRequired])
}

/** What a kitchen tells a customer about where and when it cooks. */
export interface ChefKitchen {
  /** Human area label ("Lekki, Lagos"), or empty when the kitchen has not set one. */
  area: string
  /** Parsed weekly working hours, or null when the kitchen keeps none. */
  schedule: WeeklySchedule | null
}

/**
 * `chef profile_id → where and when they cook`. Companion to useChefOpenMap,
 * which answers only "right now": planning a meal for a future day needs the
 * working week itself, and the kitchen's area, to tell the customer when the
 * food will actually reach them.
 */
export const useChefKitchens = (): Map<string, ChefKitchen> => {
  const vendors = useVendorProfileSnapshot() as { records: readonly Record<string, unknown>[] }
  return useMemo(() => {
    const map = new Map<string, ChefKitchen>()
    for (const v of vendors.records) {
      const id = String(v.profile_id ?? v.id ?? '')
      if (!id) continue
      map.set(id, {
        area: realOr(v.location_label, ''),
        schedule: parseSchedule(v.schedule),
      })
    }
    return map
  }, [vendors.records])
}

/** Live dishes use public eligibility. Demo projections remain available offline. */

/* ── field guards (shared with ChefsPage) ─────────────────────────────── */

// The prototype runtime seeds stores with placeholder rows whose string fields
// are literal tokens ("display_name-1"), and in dev it reports `live` even with
// no real backend. Treat any `<field>-<n>` token as absent.
const PLACEHOLDER_TOKEN = /^[a-z][a-z_]*-\d+$/
export const isRealValue = (value: unknown): value is string => {
  const str = typeof value === 'string' ? value.trim() : ''
  return Boolean(str) && !PLACEHOLDER_TOKEN.test(str)
}
export const realOr = (value: unknown, fallback: string): string =>
  isRealValue(value) ? value.trim() : fallback

/** Enum/text tolerant: DISH_CATEGORY_RICE → rice, "rice" → rice. */
export const enumTail = (value: unknown): string => {
  const str = typeof value === 'string' ? value.trim().toLowerCase() : ''
  if (!str) return ''
  const parts = str.split('_')
  // Enum tokens carry a domain prefix (dish_category_*, vendor_type_*); a bare
  // value like "rice" has no prefix and is returned whole.
  return parts.length > 2 ? parts.slice(2).join('_') : str
}

/**
 * Chef-type allowlist, keyed by the normalized vendor-type tail (enumTail
 * accepts both the DB value 'home_chef' and the proto enum name
 * VENDOR_TYPE_HOME_CHEF). Vendor types outside it (market_store, grocery,
 * supermarket, pharmacy, specialty) belong to the marketplace surface
 * (useUiStores) and must never render on a chef surface. Mirrors the backend
 * eligibility predicate (discovery service + discover_chefs projection).
 */
export const CHEF_CATEGORY_BY_TYPE: Record<string, string> = {
  home_chef: 'Home Chef',
  food_truck: 'Food Truck',
  bakery: 'Bakery',
  caterer: 'Caterer',
  drinks: 'Drinks',
  drinks_and_coolers: 'Drinks',
}

const normalizeCategory = (value: unknown): DishCategory => {
  const tail = String(value ?? '').trim().toLowerCase().replace(/^dish_category_/, '')
  if (tail === 'swallow' || tail === 'swallows') return 'swallow'
  if (tail === 'soups' || tail === 'soup') return 'soups'
  if (tail === 'grill' || tail === 'grills' || tail === 'suya') return 'grill'
  if (tail === 'chops' || tail === 'small_chops' || tail === 'snacks' || tail === 'pastry') return 'chops'
  if (tail === 'drinks' || tail === 'drink' || tail === 'coolers' || tail === 'beverages') return 'drinks'
  if (tail === 'breakfast') return 'breakfast'
  if (tail === 'protein' || tail === 'fish' || tail === 'meat' || tail === 'chicken') return 'protein'
  if (tail === 'rice' || tail === 'jollof') return 'rice'
  return tail || 'main'
}

export const GLYPH_BY_CATEGORY: Record<string, string> = {
  rice: '🍛',
  swallow: '🥘',
  soups: '🍲',
  grill: '🥩',
  chops: '🥟',
  drinks: '🍹',
  breakfast: '🥞',
  protein: '🍗',
  pastry: '🧁',
  sides: '🍟',
  main: '🍽️',
}

/* ── chef directory (profile_id → name + avatar) ──────────────────────── */

export interface ChefEntry {
  name: string
  avatar?: string
}

/** Resolve chef/vendor `profile_id` references onto display name + avatar. */
export const useChefDirectory = (): Map<string, ChefEntry> => {
  const profiles = useChowProfileSnapshot() as { records: readonly Record<string, unknown>[] }
  const vendors = useVendorProfileSnapshot() as { records: readonly Record<string, unknown>[] }
  return useMemo(() => {
    const photos = new Map(vendors.records.filter(row => chefPhoto(String(row.avatar_image_url ?? '')))
      .map(row => [String(row.profile_id), String(row.avatar_image_url).trim()]))
    const map = new Map<string, ChefEntry>()
    for (const r of profiles.records) {
      const id = String(r.id ?? '')
      if (!id || !isRealValue(r.display_name)) continue
      map.set(id, {
        name: displayTitle(r.display_name),
        avatar: chefPhoto(
          photos.get(id) ?? (isRealValue(r.avatar_url) ? String(r.avatar_url).trim() : undefined),
          isRealValue(r.cuisine) ? String(r.cuisine).trim() : undefined,
          String(r.display_name).trim(),
        ),
      })
    }
    return map
  }, [profiles.records, vendors.records])
}

/**
 * `merchant_profile_id → kitchen name`, for the surfaces that hold an order or
 * a cart line and only have the id. Prefers the profile directory and falls
 * back to the chef name carried on that kitchen's dishes, so offline/seed rows
 * (whose merchants are not in the profile projection) still name their kitchen
 * instead of reading "Kitchen".
 */
export const useKitchenNames = (): ((merchantId: string) => string) => {
  const directory = useChefDirectory()
  const { records: dishes } = useUiDishes()
  return useMemo(() => {
    const byMerchant = new Map<string, string>()
    for (const dish of dishes) {
      if (dish.chefId && dish.chef && !byMerchant.has(dish.chefId)) {
        byMerchant.set(dish.chefId, dish.chef)
      }
    }
    return (merchantId: string) =>
      directory.get(merchantId)?.name ?? byMerchant.get(merchantId) ?? 'Kitchen'
  }, [directory, dishes])
}

/* ── dishes ───────────────────────────────────────────────────────────── */

export interface UiDish {
  id: string
  name: string
  chef: string
  chefId: string
  chefGlyph: string
  glyph: string
  image?: string
  price: number
  category: DishCategory
  stock?: number
  status: 'available' | 'sold_out' | 'hidden'
  description: string
  /** Main ingredients, chef-curated display order. Empty = not specified. */
  ingredients: readonly string[]
  /** Allergen category slugs (see allergens.ts). Empty = none declared. */
  allergens: readonly string[]
  /** Preparation minutes; 0 = not specified. */
  cookTimeMinutes: number
  /** ISO weekdays (1=Mon…7=Sun) offered; empty = every day. */
  availableDays: readonly number[]
  /** Owning menu id; '' for standalone library dishes. */
  menuId: string
  /** The kitchen's server-derived badge tier (none when unknown). */
  chefBadge: string
  /** Absent until the public feed reports customer reviews. */
  rating?: number
}

export const useUiDishes = (): { records: readonly UiDish[]; live: boolean; loading?: boolean } => {
  const snap = useDishSnapshot() as { records: readonly Record<string, unknown>[] }
  const directory = useChefDirectory()
  const verification = useChefVerification()
  const { dishes: publicDishes, loading: publicLoading } = usePublicDishes(100)

  return useMemo(() => {
    const live = isLiveRuntime()
    const chefLoaded = directory.size > 0
    const seen = new Set<string>()
    const realSnapRecords = snap.records.filter((r) => isRealValue(r.name))

    if (!live && realSnapRecords.length > 0) {
      const records = realSnapRecords
        .filter((r) => String(r.status || 'available') !== 'hidden')
        .filter((r) => !chefLoaded || directory.has(String(r.chef_profile_id ?? '')))
        .filter((r) => verification.size === 0 || isSellingKitchen(verification.get(String(r.chef_profile_id ?? ''))))
        .map((r, index) => {
          const chef = directory.get(String(r.chef_profile_id ?? ''))
          const category = normalizeCategory(r.category)
          const rawStatus = String(r.status || 'available') as 'available' | 'sold_out' | 'hidden'
          const meta = (r.metadata ?? {}) as Record<string, unknown>
          const stock = typeof meta.stock === 'number' ? meta.stock : (rawStatus === 'sold_out' || r.stock_quantity === 0 ? 0 : undefined)
          return {
            id: String(r.id ?? index),
            name: displayTitle(r.name),
            chef: chef?.name ?? 'Kitchen',
            chefId: String(r.chef_profile_id ?? 'merchant-prototype'),
            chefGlyph: '👨🏾‍🍳',
            glyph: GLYPH_BY_CATEGORY[enumTail(r.category)] ?? '🍽️',
            image: dishPhoto(
              isRealValue(r.image_url) ? String(r.image_url).trim() : undefined,
              category,
              String(r.name).trim(),
            ),
            price: Number(r.price_minor ?? 0) / 100,
            category,
            stock,
            status: rawStatus,
            description: isRealValue(r.description) ? String(r.description).trim() : '',
            ingredients: stringArray(r.ingredients),
            allergens: stringArray(r.allergens),
            cookTimeMinutes: Number(r.cook_time_minutes ?? 0) || 0,
            availableDays: numberArray(r.available_days),
            menuId: isRealValue(r.menu_id) ? String(r.menu_id) : '',
            chefBadge: verification.get(String(r.chef_profile_id ?? ''))?.badge ?? 'none',
          }
        })
        .filter((d) => {
          const key = `${d.name.toLowerCase()}::${d.chefId}::${d.menuId}`
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
      return { records, live }
    }

    if (publicDishes.length > 0) {
      const records = publicDishes.map((d, index) => {
        const category = normalizeCategory(d.category)
        return {
          id: d.id || `public-dish-${index}`,
          name: displayTitle(d.name),
          chef: displayTitle(d.chef) || 'Kitchen',
          chefId: d.chef_profile_id || d.chef_id || 'merchant-prototype',
          chefGlyph: '👨🏾‍🍳',
          glyph: GLYPH_BY_CATEGORY[enumTail(d.category)] ?? '🍽️',
          image: isRealValue(d.image) ? d.image.trim() : undefined,
          price: Number(d.price_minor ?? 0) / 100,
          category,
          stock: d.stock_quantity,
          status: (d.status as 'available' | 'sold_out' | 'hidden') || 'available',
          description: d.description || '',
          ingredients: d.ingredients || [],
          allergens: d.allergens || [],
          cookTimeMinutes: Number(d.cook_time_minutes ?? 0) || 0,
          availableDays: (d.available_days || []).map(Number),
          menuId: d.menu_id || '',
          chefBadge: d.chef_badge || 'none',
          rating: d.reviews > 0 && Number.isFinite(d.rating) && d.rating > 0 && d.rating <= 5 ? d.rating : undefined,
        }
      })
      return { records, live }
    }

    // The public catalogue is still in flight: say so rather than flash the
    // curated seed rows (dev) or an empty list (deploy) before it lands.
    if (publicLoading) return { records: [], live, loading: true }

    if (!live && allowSeedContent()) {
      const records = curatedDishes.map((d, index) => {
        const menuId =
          d.chef === 'Amara Okafor'
            ? 'menu-amara'
            : d.chef === 'Chef Tunde'
            ? 'menu-tunde'
            : d.chef === "Laila's Patisserie"
            ? 'menu-laila'
            : ''
        return {
          id: `dish-${index}`,
          name: d.name,
          chef: d.chef,
          chefId: `merchant-${d.chef.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
          chefGlyph: d.chefGlyph,
          glyph: d.glyph,
          image: dishPhoto(d.image, d.category, d.name),
          price: d.price,
          category: d.category,
          stock: d.stock,
          status: d.status ?? 'available',
          description: '',
          ingredients: [],
          allergens: [],
          cookTimeMinutes: 0,
          availableDays: [],
          menuId,
          chefBadge: 'verified',
        }
      })
      return { records, live: false }
    }

    return { records: [], live, loading: publicLoading }
  }, [snap.records, publicDishes, publicLoading, directory, verification])
}

const stringArray = (raw: unknown): readonly string[] =>
  Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string' && v.trim() !== '') : []

const numberArray = (raw: unknown): readonly number[] =>
  Array.isArray(raw)
    ? raw.map((v) => Number(v)).filter((n) => Number.isFinite(n) && n >= 1 && n <= 7)
    : []

/** One dish by id or slug, resolving across both live projections and public catalog. */
export const useUiDish = (dishId: string): UiDish | undefined => {
  const { records } = useUiDishes()
  return useMemo(() => {
    if (!dishId) return undefined
    const clean = dishId.trim().toLowerCase()
    const targetTail = clean.length >= 8 ? clean.slice(-8) : clean
    return records.find((d) => {
      const id = d.id.toLowerCase()
      if (id === clean) return true
      if (canonicalSlug(d.name, d.id).toLowerCase() === clean) return true
      if (slugify(d.name).toLowerCase() === clean) return true
      if (targetTail && (id.startsWith(targetTail) || id.endsWith(targetTail))) return true
      return false
    })
  }, [records, dishId])
}

/**
 * The dish ids this profile has starred. Reads the dish_favourites projection
 * so a star made on one surface (dish row, detail page) is reflected on every
 * other without a refetch.
 */
export const useMyFavouriteDishIds = (profileId: string): ReadonlySet<string> => {
  const snap = useDishFavouriteSnapshot() as { records: readonly Record<string, unknown>[] }
  return useMemo(() => {
    const ids = new Set<string>()
    for (const row of snap.records) {
      if (String(row.profile_id ?? '') !== profileId) continue
      const dishId = String(row.dish_id ?? '')
      if (dishId) ids.add(dishId)
    }
    return ids
  }, [snap.records, profileId])
}

/**
 * The signed-in eater's allergy categories.
 *
 * Read through the authorized `profile:get_profile` command, not the profile
 * projection. That projection is the marketplace's name-and-face directory —
 * tenant-wide by necessity, because every dish card resolves "by <chef>"
 * through it — so it deliberately no longer carries allergies, phone, email or
 * city (see internal/startup/projection_fetcher.go, profileDirectoryFields).
 * Health data does not belong on a wire every signed-in device is subscribed
 * to; the command answers only for the caller's own profile.
 *
 * One fetch is shared by every consumer of the hook and cached for the session,
 * refreshed in place when the eater saves (see setMyAllergies).
 */
const allergyCache = new Map<string, readonly string[]>()
const allergyListeners = new Set<() => void>()
let allergyRevision = 0
const pendingAllergyFetch = new Set<string>()

const notifyAllergyChange = () => {
  allergyRevision += 1
  allergyListeners.forEach((listener) => listener())
}

/** Publish what the server just stored, so the UI reflects a save at once. */
export const setMyAllergies = (profileId: string, allergies: readonly string[]): void => {
  if (!profileId) return
  allergyCache.set(profileId, [...allergies])
  notifyAllergyChange()
}

const subscribeAllergies = (listener: () => void): (() => void) => {
  allergyListeners.add(listener)
  return () => {
    allergyListeners.delete(listener)
  }
}

export const useMyAllergies = (profileId: string): readonly string[] => {
  const profiles = useChowProfileSnapshot() as { records: readonly Record<string, unknown>[] }
  const revision = useSyncExternalStore(
    subscribeAllergies,
    () => allergyRevision,
    () => allergyRevision,
  )

  useEffect(() => {
    if (!isLiveRuntime() || !profileId) return
    if (allergyCache.has(profileId) || pendingAllergyFetch.has(profileId)) return
    pendingAllergyFetch.add(profileId)
    void commands.profile
      .getProfile(profileId)
      .then((res) => {
        const row = commandPayload(res)
        setMyAllergies(profileId, stringArray(row.allergies))
      })
      .catch(() => {
        // Unreadable (offline, or not this caller's profile): treat as none
        // declared rather than blocking the screen. Warnings fall silent, which
        // is why the fetch is retried on the next mount rather than cached.
        pendingAllergyFetch.delete(profileId)
      })
      .finally(() => {
        pendingAllergyFetch.delete(profileId)
      })
  }, [profileId])

  return useMemo(() => {
    if (isLiveRuntime()) {
      void revision
      return allergyCache.get(profileId) ?? []
    }
    // Offline prototype: the seeded profile row still carries the field.
    const mine = profiles.records.find((r) => String(r.id ?? '') === profileId)
    return stringArray(mine?.allergies)
  }, [profiles.records, profileId, revision])
}

/* ── vendor locations ─────────────────────────────────────────────────── */

export interface UiVendorLocation {
  id: string
  profileId: string
  label: string
  address: string
  city: string
  area: string
  /** Catalogue city ("gn:<id>", migration 000032); "" until filed. */
  cityRef: string
  state: string
  latitude: number
  longitude: number
  isPrimary: boolean
}

/** "Lekki, Lagos" style display line for one location. */
export const locationLine = (loc: UiVendorLocation): string => {
  if (loc.area && loc.city) return `${loc.area}, ${loc.city}`
  return loc.area || loc.city || loc.label
}

/** A vendor's locations, primary first. Empty until the chef has added any. */
export const useVendorLocations = (profileId: string): readonly UiVendorLocation[] => {
  const snap = useVendorLocationSnapshot() as { records: readonly Record<string, unknown>[] }
  return useMemo(
    () =>
      snap.records
        .filter((r) => String(r.profile_id ?? '') === profileId && isRealValue(r.label))
        .map((r) => ({
          id: String(r.id ?? ''),
          profileId: String(r.profile_id ?? ''),
          label: String(r.label ?? '').trim(),
          address: isRealValue(r.address) ? String(r.address).trim() : '',
          city: isRealValue(r.city) ? String(r.city).trim() : '',
          area: isRealValue(r.area) ? String(r.area).trim() : '',
          cityRef: typeof r.city_ref === 'string' ? r.city_ref : '',
          state: typeof r.state === 'string' ? r.state : '',
          latitude: Number(r.latitude ?? 0) || 0,
          longitude: Number(r.longitude ?? 0) || 0,
          isPrimary: r.is_primary === true,
        }))
        .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary)),
    [snap.records, profileId],
  )
}

/* ── market stores ────────────────────────────────────────────────────── */

const STORE_TYPE_BY_ENUM: Record<string, Store['type']> = {
  market_store: 'market',
  grocery: 'grocery',
  supermarket: 'supermarket',
  pharmacy: 'pharmacy',
}

const STORE_PALETTE: Record<Store['type'], Store['palette']> = {
  market: 'red',
  grocery: 'green',
  supermarket: 'orange',
  pharmacy: 'green',
}

const STORE_CATEGORY_LABEL: Record<Store['type'], string> = {
  market: 'Market',
  grocery: 'Grocery',
  supermarket: 'Supermarket',
  pharmacy: 'Pharmacy',
}

export const useUiStores = (): { records: readonly Store[]; live: boolean } => {
  const vendors = useVendorProfileSnapshot() as { records: readonly Record<string, unknown>[] }

  return useMemo(() => {
    const live = isLiveRuntime()
    if (!live && allowSeedContent()) {
      return { records: curatedStores, live: false }
    }
    const records: Store[] = []
    vendors.records.forEach((r) => {
      const type = STORE_TYPE_BY_ENUM[enumTail(r.vendor_type)]
      if (!type || !isRealValue(r.display_name)) return
      // vendor_profiles carries identity + open-state; the marketplace-facts
      // (hours/eta/basket size/min order) are not projected yet, so they take
      // sensible defaults the storefront UI requires until a richer read lands.
      records.push({
        type,
        category: STORE_CATEGORY_LABEL[type],
        name: displayTitle(r.display_name),
        glyph: '🏪',
        area: realOr(r.location_label, 'Nearby'),
        status: r.is_open ? 'open' : 'closed',
        hours: r.is_open ? 'Open now' : 'Closed',
        rating: 4.6,
        etaMins: '25–45 min',
        items: 12,
        minOrder: 2000,
        palette: STORE_PALETTE[type],
      })
    })
    return { records, live }
  }, [vendors.records])
}
